<template>
  <!-- 新增/修改配合费用弹窗 -->
  <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="600px" :close-on-click-modal="false" @close="handleClose">
    <!-- 配合费用表单 -->
    <el-form :model="formData" :rules="rules" ref="feeForm" label-width="120px">
      <!-- 批量保存时显示识别码 -->
      <el-form-item label="识别码" v-if="dialogType === 'batch'">
        <el-input :value="selectedData.map(item => item.oidNo).join(', ')" disabled></el-input>
      </el-form-item>

      <!-- 单条修改时显示识别码 -->
      <el-form-item label="识别码" v-if="dialogType === 'edit'">
        <el-input v-model="formData.oidNo" disabled></el-input>
      </el-form-item>

      <!-- 配合费（可编辑，必填） -->
      <el-form-item label="配合费" prop="cooperationFee">
        <el-input v-model="formData.cooperationFee" placeholder="请输入配合费">
          <template slot="append">元</template>
        </el-input>
      </el-form-item>

      <!-- 操作员（自动填充，禁用编辑） -->
      <el-form-item label="操作员" prop="operator">
        <el-input v-model="formData.operator" disabled></el-input>
      </el-form-item>

      <!-- 操作时间（自动填充，禁用编辑） -->
      <el-form-item label="操作时间" prop="operateTime">
        <el-input v-model="formData.operateTime" disabled></el-input>
      </el-form-item>

      <!-- 费用内容备注 -->
      <el-form-item label="费用内容备注" prop="feeContentRemark">
        <el-input v-model="formData.feeContentRemark" placeholder="请输入费用内容备注"></el-input>
      </el-form-item>
    </el-form>
    
    <!-- 弹窗底部按钮 -->
    <div slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取消</el-button>
      <el-button type="primary" @click="handleSave" :loading="saveLoading">保存</el-button>
    </div>
  </el-dialog>
</template>

<script>
// 导入保存和修改API
import { saveCoordinationFee, updateCoordinationFee } from '@/api/contract/contract'

export default {
  name: 'CoordinationFeeDialog',
  data () {
    return {
      // 弹窗显示状态
      dialogVisible: false,
      // 保存按钮加载状态
      saveLoading: false,
      // 弹窗类型：batch-批量保存，edit-单条修改
      dialogType: 'batch',
      // 选中的数据列表
      selectedData: [],
      // 表单数据
      formData: {
        oidNo: '',              // 识别码（仅修改时显示）
        cooperationFee: '',     // 配合费
        operator: '',           // 操作员
        operateTime: '',         // 操作时间
        feeContentRemark: ''    // 费用内容备注
      },
      // 表单验证规则
      rules: {
        cooperationFee: [
          { required: true, message: '请输入配合费', trigger: 'blur' },
          { pattern: /^[0-9]+(\.[0-9]{1,2})?$/, message: '请输入正确的金额格式', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    /**
     * 弹窗标题
     */
    dialogTitle () {
      return this.dialogType === 'batch' ? '批量保存配合费' : '修改配合费'
    }
  },
  methods: {
    /**
     * 打开弹窗
     * @param {Array} selectedRows - 选中的数据行数组
     * @param {String} type - 弹窗类型：batch-批量保存，edit-单条修改
     */
    openDialog (selectedRows, type) {
      // 检查是否有上个月的数据
      const now = new Date()
      const currentYear = now.getFullYear()
      const currentMonth = now.getMonth() + 1

      // 检查选中数据的检测时间
      const hasLastMonthData = selectedRows.some(row => {
        if (!row.testDate) return false

        const testDate = new Date(row.testDate)
        const testYear = testDate.getFullYear()
        const testMonth = testDate.getMonth() + 1

        // 判断是否是上个月或更早的数据
        if (testYear < currentYear) {
          return true
        } else if (testYear === currentYear && testMonth < currentMonth) {
          return true
        }
        return false
      })

      // 如果有上个月的数据，提示不能录入
      if (hasLastMonthData) {
        this.$message.warning('所选数据中存在上个月的记录，不能录入配合费')
        return
      }

      // 设置弹窗类型和选中数据
      this.dialogType = type
      this.selectedData = selectedRows
      this.dialogVisible = true

      // 如果是单条修改，填充识别码、配合费和费用内容备注
      if (type === 'edit' && selectedRows.length > 0) {
        const row = selectedRows[0]
        this.formData.oidNo = row.oidNo || ''
        this.formData.cooperationFee = row.cooperationFee || ''
        this.formData.feeContentRemark = row.feeContentRemark || ''
      } else {
        this.formData.cooperationFee = ''
        this.formData.feeContentRemark = ''
      }

      // 设置默认操作员（从store中获取当前登录用户）
      this.formData.operator = this.$store.state.loginUser.userName || ''

      // 设置默认操作时间为当天
      const today = new Date()
      const year = today.getFullYear()
      const month = String(today.getMonth() + 1).padStart(2, '0')
      const day = String(today.getDate()).padStart(2, '0')
      this.formData.operateTime = `${year}-${month}-${day}`
    },

    /**
     * 保存
     */
    handleSave () {
      // 表单验证
      this.$refs.feeForm.validate((valid) => {
        if (valid) {
          this.saveLoading = true

          if (this.dialogType === 'batch') {
            // 批量保存
            this.handleBatchSave()
          } else {
            // 单条修改
            this.handleUpdate()
          }
        } else {
          this.$message.warning('请填写完整信息')
          return false
        }
      })
    },

    /**
     * 批量保存
     */
    handleBatchSave () {
      // 构建批量保存数据数组
      const saveDataList = this.selectedData.map(row => ({
        oidNo: row.oidNo,
        entrustNum: row.entrustNum,
        cooperationFee: parseFloat(this.formData.cooperationFee),
        feeContentRemark: this.formData.feeContentRemark

      }))

      // 调用批量保存接口（直接传数组）
      saveCoordinationFee(saveDataList).then(res => {
        this.saveLoading = false
        if (res.retCode === 'ok') {
          this.$message.success(res.retMsg)
          this.handleClose()
          // 通知父组件刷新列表
          this.$emit('refresh')
        } else {
          this.$message.error(res.retMsg || '保存失败')
        }
      }).catch(error => {
        this.saveLoading = false
        console.error('保存失败:', error)
        this.$message.error('保存失败')
      })
    },

    /**
     * 单条修改
     */
    handleUpdate () {
      // 构建修改数据
      const row = this.selectedData[0]
      const updateData = {
        feeId: row.feeId,
        oidNo: row.oidNo,
        entrustNum: row.entrustNum,
        cooperationFee: parseFloat(this.formData.cooperationFee),
        feeContentRemark: this.formData.feeContentRemark
      }

      // 调用修改接口
      updateCoordinationFee(updateData).then(res => {
        this.saveLoading = false
        if (res.retCode === 'ok') {
          this.$message.success('修改成功')
          this.handleClose()
          // 通知父组件刷新列表
          this.$emit('refresh')
        } else {
          this.$message.error(res.retMsg || '修改失败')
        }
      }).catch(error => {
        this.saveLoading = false
        console.error('修改失败:', error)
        this.$message.error('修改失败')
      })
    },

    /**
     * 关闭弹窗
     * 清除数据并恢复默认状态
     */
    handleClose () {
      this.dialogVisible = false
      // 重置表单
      this.$refs.feeForm.resetFields()
      // 清空数据
      this.selectedData = []
      this.formData = {
        oidNo: '',
        cooperationFee: '',
        operator: '',
        operateTime: '',
        feeContentRemark: ''
      }
    }
  }
}
</script>

<style scoped>
/* 弹窗底部按钮样式 */
.dialog-footer {
  text-align: right;
}
</style>
